<template>
    <sidebar-menu
        :items="sidebarMenuItems"
        has-icon
        :force-expanded="forceExpanded"
        @update:expanded="$emit('update:expanded', $event)"
    />
</template>

<script>
import { mapState } from 'vuex';

import SidebarMenu from '@/components/sidebar/SidebarMenu.vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';

export default {
    name: 'UserSideBar',

    components: { SidebarMenu },

    emits: ['update:expanded'],

    props: {
        forceExpanded: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        userPermission() {
            return (
                this.user.hasPermissionTo('view removed course enrollments') ||
                this.user.hasGroupPermissionTo('view removed course enrollments')
            );
        },

        canViewEnrollmentsOnProfile() {
            return (
                this.userPermission ||
                this.user.hasPermissionTo('view enrollments on profile page') ||
                this.user.hasGroupPermissionTo('view enrollments on profile page')
            );
        },

        canViewRemediation() {
            return (
                this.user.hasPermissionTo('view all remediation processes') ||
                this.user.hasGroupPermissionTo('view all remediation processes')
            );
        },

        canViewGrades() {
            return (
                this.user.hasPermissionTo('view all course grade') ||
                this.user.hasGroupPermissionTo('view all course grade')
            );
        },

        canViewOtherUserEvaluationDashboard() {
            return (
                this.user.hasPermissionTo("view another user's evaluation dashboard") ||
                this.user.hasGroupPermissionTo("view another user's evaluation dashboard") ||
                this.user.isGroupInstructor()
            );
        },

        // The evaluation dashboard shows instructor feedback, so it is only
        // relevant for users who teach in some capacity. Pure students should
        // not see the link (and are blocked from the route). Multi-role users
        // who are also instructors keep access.
        canViewOwnEvaluations() {
            return this.user.is_instructor || this.user.isInstructorOfActiveCourse() || this.user.isGroupInstructor();
        },

        isViewingOwnProfile() {
            return this.$route.matched[0].path === '/me';
        },

        canViewOwnEbooks() {
            return this.$gate.allows('viewAny', 'Ebook') || this.user.hasRole('Student');
        },

        canViewOtherUserEbooks() {
            return this.$gate.allows('viewAny', 'Ebook');
        },

        showInstitutionTranscriptLink() {
            return !!(
                this.$store.getters['InstitutionSettings/get']('transcript_link') &&
                this.user.hasRole('Student') &&
                !this.user.is_faculty
            );
        },

        sidebarMenuItems() {
            let sidebarItems = [];
            const isCurrentUserRoute = this.$route.matched[0]?.path === '/me';

            // While navigating away from /me or /people/:personId (e.g. to
            // /home), this computed can re-run before the component unmounts.
            // At that point matched[0] is no longer '/me' and
            // route.params.personId is undefined, so the "other user" branch
            // below would build person-* RouterLinks with a missing required
            // `personId` param and throw. Bail out when there is no valid
            // person context to render.
            if (!isCurrentUserRoute && !this.$route.params.personId) {
                return sidebarItems;
            }

            this.addProfileMenuItem(sidebarItems, isCurrentUserRoute);

            // Corporate profile UX is stripped down, but the two contexts
            // differ:
            //  - Self profile (/me): only the Profile entry renders here (the
            //    "Home" entry lives in the global app navigation, not here).
            //    All the Stanbridge-only sub-pages are suppressed.
            //  - Admin viewing another user (/people/:personId): admins still
            //    need to manage that user, so we surface a focused subset —
            //    Enrollments and Access alongside Profile.
            if (isCorporateClient()) {
                if (!isCurrentUserRoute) {
                    this.addCorporateOtherUserMenuItems(sidebarItems);
                }

                return sidebarItems.filter(item => item !== null);
            }

            if (isCurrentUserRoute) {
                this.addCurrentUserSpecificMenuItems(sidebarItems);
            } else {
                this.addOtherUserSpecificMenuItems(sidebarItems);
            }

            sidebarItems = sidebarItems.filter(item => item !== null);
            return sidebarItems;
        },
    },

    methods: {
        addProfileMenuItem(sidebarItems, isCurrentUserRoute) {
            const profileRoute = isCurrentUserRoute
                ? { name: 'me' }
                : { name: 'person-view', params: { personId: this.$route.params.personId } };

            const profileMenuItem = this.createSidebarMenuItem(
                'Profile',
                'user',
                profileRoute,
                this.$gate.allows('view', ['Status'])
            );

            sidebarItems.push(profileMenuItem);
        },

        addCurrentUserSpecificMenuItems(sidebarItems) {
            const currentUserMenuItems = [
                this.createSidebarMenuItem('Enrollments', 'pi pi-graduation-cap', { name: 'my-enrollments' }),
                this.createSidebarMenuItem('Surveys', 'pi pi-pencil', { name: 'my-surveys' }),
                this.canViewOwnEbooks ? this.createSidebarMenuItem('Ebooks', 'book', { name: 'my-ebook' }) : null,
                this.createSidebarMenuItem('Grades', 'pi pi-chart-bar', { name: 'my-grades' }),

                this.$hasFeature('INBOX')
                    ? this.createSidebarMenuItem('Notifications', 'pi pi-globe', { name: 'my-notifications' })
                    : null,

                this.userPermission ? this.createSidebarMenuItem('Access', 'pi pi-lock', { name: 'my-access' }) : null,

                this.showInstitutionTranscriptLink
                    ? this.createSidebarMenuItem('Transcript', 'pi pi-receipt', { name: 'my-transcript' })
                    : null,

                this.canViewOwnEvaluations
                    ? this.createSidebarMenuItem('Evaluations', 'pi pi-file', {
                          name: 'evaluation-instructor',
                          params: { userId: this.isViewingOwnProfile ? this.user.id : this.$route.params.personId },
                          query: { from: 'profile' },
                      })
                    : null,
            ];

            sidebarItems.push(...currentUserMenuItems.filter(item => item !== null));
        },

        addOtherUserSpecificMenuItems(sidebarItems) {
            const otherUserMenuItems = [
                this.createSidebarMenuItem(
                    'Enrollments',
                    'graduation-cap',
                    { name: 'person-enrollments' },
                    this.canViewEnrollmentsOnProfile
                ),
                this.canViewGrades
                    ? this.createSidebarMenuItem('Grades', 'pi pi-chart-line', { name: 'person-grades' })
                    : null,

                this.$hasFeature('INBOX') && this.userPermission
                    ? this.createSidebarMenuItem('Notifications', 'globe', {
                          name: 'person-notifications',
                          params: { personId: this.$route.params.personId },
                      })
                    : null,

                this.canViewOtherUserEbooks
                    ? this.createSidebarMenuItem('Ebooks', 'book', { name: 'person-ebook' })
                    : null,
                this.userPermission ? this.createSidebarMenuItem('Access', 'lock', { name: 'person-access' }) : null,
                this.userPermission
                    ? this.createSidebarMenuItem('Risk Assessment', 'pi pi-chart-scatter', {
                          name: 'person-risk-assessment',
                      })
                    : null,

                this.canViewRemediation
                    ? this.createSidebarMenuItem('Remediation', 'pi pi-file-check', { name: 'person-remediations' })
                    : null,

                this.canViewOtherUserEvaluationDashboard
                    ? this.createSidebarMenuItem('Evaluations', 'pi pi-file', {
                          name: 'evaluation-instructor',
                          params: { userId: this.isViewingOwnProfile ? this.user.id : this.$route.params.personId },
                          query: { from: 'profile' },
                      })
                    : null,
            ];

            sidebarItems.push(...otherUserMenuItems.filter(item => item !== null));
        },

        addCorporateOtherUserMenuItems(sidebarItems) {
            const corporateMenuItems = [
                this.createSidebarMenuItem(
                    'Enrollments',
                    'graduation-cap',
                    { name: 'person-enrollments' },
                    this.userPermission
                ),
                this.createSidebarMenuItem('Access', 'lock', { name: 'person-access' }),
            ];

            sidebarItems.push(...corporateMenuItems.filter(item => item !== null));
        },

        createSidebarMenuItem(title, icon, route, condition = true) {
            return condition ? { type: 'item', title, icon, route } : null;
        },
    },
};
</script>
